/**
 * Date and Time Formatting Utilities
 *
 * This module handles formatting of dates and timestamps with proper timezone handling.
 *
 * - Dates (YYYY-MM-DD): Calendar dates without time, displayed consistently across timezones
 * - Timestamps: Date + time, displayed in user's local timezone
 */

/**
 * Format a date string (YYYY-MM-DD or MM/DD/YYYY) as a localized date
 * without timezone conversion.
 *
 * Use this for: expiration dates, production dates, birth dates, etc.
 *
 * @example
 * formatDate('2024-11-08') // "Nov 8, 2024"
 * formatDate('11/08/2024') // "Nov 8, 2024"
 */
export const formatDate = (
  dateString: string | Date,
  format: 'short' | 'long' = 'short'
): string => {
  if (!dateString) return '-';

  let dateObj: Date;

  if (typeof dateString === 'string') {
    // Parse as local date to avoid timezone issues
    const [year, month, day] = dateString.includes('-')
      ? dateString.split('-')
      : dateString.split('/').reverse();
    dateObj = new Date(parseInt(year || '0'), parseInt(month || '0') - 1, parseInt(day || '0'));
  } else {
    dateObj = dateString;
  }

  if (format === 'short') {
    // "Nov 8, 2024"
    return dateObj.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } else {
    // "November 8, 2024"
    return dateObj.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }
};

/**
 * The DAY a timestamp fell on, in the reader's timezone, with the year left off when it is the
 * current one. "Sep 21" this year, "Sep 21, 2025" in any other.
 *
 * Use this for a record stamp shown next to its own record — a completion date, a recorded-on
 * date — where the year is noise until it isn't.
 *
 * ⚠️ **Do NOT route a timestamp through `formatDate`.** That function hand-parses the string by
 * splitting on `-`, which takes the **UTC** calendar parts: a log recorded 2026-09-21 17:10 PT is
 * `2026-09-22T00:10Z` and comes back as **Sep 22**. `new Date(iso)` then `toLocaleDateString`
 * reads it in the reader's own timezone, which is the only correct answer for a `timestamptz`.
 * `formatDate` is for date-ONLY columns, where there is no timezone to get wrong.
 *
 * @example
 * formatShortDay('2026-09-21T23:52:42Z') // "Sep 21"  (read in 2026, from PT)
 * formatShortDay('2025-09-21T23:52:42Z') // "Sep 21, 2025"
 */
export const formatShortDay = (timestamp: string | Date): string => {
  const dateObj = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  if (Number.isNaN(dateObj.getTime())) return '-';

  return dateObj.toLocaleDateString(
    'en-US',
    dateObj.getFullYear() === new Date().getFullYear()
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' }
  );
};

/**
 * Format a timestamp string (ISO 8601) in the user's local timezone.
 *
 * Use this for: created_at, updated_at, transaction timestamps, etc.
 *
 * @example
 * // User in PT (UTC-8):
 * formatTimestamp('2024-11-08T20:00:00Z') // "Nov 8, 2024 12:00 PM PST"
 *
 * // Same user in ET (UTC-5):
 * formatTimestamp('2024-11-08T20:00:00Z') // "Nov 8, 2024 3:00 PM EST"
 */
export const formatTimestamp = (
  timestamp: string | Date,
  options: {
    showDate?: boolean;
    showTime?: boolean;
    showTimezone?: boolean;
    format?: 'short' | 'long';
  } = {}
): string => {
  const {
    showDate = true,
    showTime = true,
    showTimezone = true,
    format = 'short'
  } = options;

  if (!timestamp) return '-';

  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;

  // Build the format options
  const dateOptions: Intl.DateTimeFormatOptions = {};

  if (showDate) {
    dateOptions.month = format === 'short' ? 'short' : 'long';
    dateOptions.day = 'numeric';
    dateOptions.year = 'numeric';
  }

  if (showTime) {
    dateOptions.hour = 'numeric';
    dateOptions.minute = '2-digit';
  }

  if (showTimezone) {
    dateOptions.timeZoneName = 'short';
  }

  return date.toLocaleString('en-US', dateOptions);
};

/**
 * Format a timestamp as relative time (e.g., "2 hours ago", "3 days ago")
 *
 * Use this for: activity feeds, recent updates, etc.
 *
 * @example
 * formatRelativeTime('2024-11-08T18:00:00Z') // "2 hours ago"
 */
export const formatRelativeTime = (timestamp: string | Date): string => {
  if (!timestamp) return '-';

  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffSeconds < 60) {
    return 'just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  } else if (diffWeeks < 4) {
    return `${diffWeeks} week${diffWeeks === 1 ? '' : 's'} ago`;
  } else if (diffMonths < 12) {
    return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
  } else {
    return `${diffYears} year${diffYears === 1 ? '' : 's'} ago`;
  }
};

/**
 * Get the user's timezone name
 *
 * @example
 * getUserTimezone() // "America/Los_Angeles"
 */
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Get the user's timezone abbreviation
 *
 * @example
 * getUserTimezoneAbbr() // "PST" or "PDT"
 */
export const getUserTimezoneAbbr = (): string => {
  const date = new Date();
  const formatted = date.toLocaleString('en-US', { timeZoneName: 'short' });
  const match = formatted.match(/([A-Z]{3,4})$/);
  return match?.[1] || '';
};

/**
 * Get today's date in the user's local timezone as YYYY-MM-DD
 *
 * Unlike new Date().toISOString().split('T')[0], which gives UTC date,
 * this returns the date in the user's actual timezone.
 *
 * @example
 * // When it's Nov 6, 2025 at 7:37 PM PST (which is Nov 7, 2025 at 3:37 AM UTC):
 * new Date().toISOString().split('T')[0] // "2025-11-07" (UTC date - WRONG for user)
 * getLocalDateString() // "2025-11-06" (PST date - CORRECT for user)
 */
export const getLocalDateString = (date?: Date): string => {
  const d = date ?? new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse a date-only string (YYYY-MM-DD) as local time instead of UTC.
 *
 * When JavaScript parses a date-only string like "2025-01-19", it interprets
 * it as midnight UTC. In US timezones, this shows as the previous day.
 *
 * This function parses the date as local midnight to avoid off-by-one issues.
 *
 * @example
 * // In PST (UTC-8):
 * new Date('2025-01-19') // Jan 18, 2025 4:00 PM PST (WRONG - shifted back a day)
 * parseLocalDate('2025-01-19') // Jan 19, 2025 12:00 AM PST (CORRECT)
 */
export const parseLocalDate = (dateString: string): Date => {
  // Append T00:00:00 to interpret as local midnight instead of UTC midnight
  return new Date(dateString + 'T00:00:00');
};

/**
 * Normalise a `DateField` value to the `YYYY-MM-DD` every other layer speaks.
 *
 * ⚠️ **`DateField` emits `MM/DD/YYYY` from BOTH of its paths** — typing
 * (`DateField.tsx:348`) and the calendar (`Calendar.tsx:141-147`) — while
 * payloads, RPC parameters, URL segments and `parseLocalDate` are all
 * `YYYY-MM-DD`. Every consumer therefore needs a conversion at the boundary,
 * and this is it. DESIGN.md documents the gotcha; this is the fix.
 *
 * **What goes wrong without it is not only the write.** A slash value compares
 * as a STRING against an ISO one and always loses (`'2026-08-17' < '08/17/2026'`
 * is false, because `'2' > '0'`), and two slash values compare wrongly across a
 * year boundary (`'12/01/2026' < '01/05/2027'` is false). So a form that loads
 * ISO from the database and receives slashes from the field has two values of
 * the same date that no comparison can order — which is exactly what the
 * recurring editor's end-date-before-start-date check was reading.
 *
 * Writes have been surviving on luck: Postgres runs `DateStyle = ISO, MDY`, so
 * `'08/17/2026'::date` parses correctly on the way in. That is a property of the
 * server's configuration, not of the value.
 *
 * Returns `null` for empty input, so a cleared field means "no date" rather than
 * an empty string. Anything unparseable is passed through unchanged — this
 * function normalises, it does not validate.
 *
 * @example
 * toIsoDateString('08/17/2026') // "2026-08-17"
 * toIsoDateString('2026-08-17') // "2026-08-17"  (already ISO, untouched)
 * toIsoDateString('')           // null
 */
export const toIsoDateString = (value: string | null | undefined): string | null => {
  if (!value || !value.trim()) return null;
  const input = value.trim();

  // Already ISO — the load path, and re-entrant renders of a converted value.
  if (/^\d{4}-\d{2}-\d{2}$/.test(input)) return input;

  const slashed = input.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashed) {
    const [, month, day, year] = slashed;
    return `${year}-${month!.padStart(2, '0')}-${day!.padStart(2, '0')}`;
  }

  return input;
};
